import { useWSSnackbar, WSElement } from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { ICOIExtractionInterface } from "@wingspanhq/shared-files/dist/lib/constants/extractions";
import {
  ISharedFileRequestResponse,
  SharedFileRequestStatus
} from "@wingspanhq/shared-files/dist/lib/interfaces";
import { useHistory } from "react-router-dom";
import { useDownloadPrivateDocument } from "../../../../../../query/files/mutations";
import { filesService } from "../../../../../../services/files";
import { downloadFileFromBuffer } from "../../../../../../utils/files";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import { selectorIsRequirementReadyToUploadFile } from "../../../../selectors/selectorIsRequirementReadyToUploadFile";
import { selectorIsRequirementRejected } from "../../../../selectors/selectorIsRequirementRejected";
import { selectorIsSharedFileCOIContext } from "../../../../selectors/selectorIsSharedFileCOIContext";
import { selectorIsSharedFileNotNoneContext } from "../../../../selectors/selectorIsSharedFileNotNoneContext";
import { selectorIsSharedFileReUploadAvailable } from "../../../../selectors/selectorIsSharedFileReUploadAvailable";
import { selectorIsSharedFileReUploadFlow } from "../../../../selectors/selectorIsSharedFileReUploadFlow";
import { selectorSharedFileExtractedFields } from "../../../../selectors/selectorSharedFileExtractedFields";
import { selectorSharedFileReUploadAttempts } from "../../../../selectors/selectorSharedFileReUploadAttempts";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { SharedFileDetailsWidgetsContainer } from "../../components/SharedFileDetailsWidgetsContainer";
import { SharedFileInstructionsWidgetsContainer } from "../../components/SharedFileInstructionsWidgetsContainer";
import { SharedFilePolicies } from "../../components/SharedFilePolicies";
import { useSubmitSharedFileModal } from "../../components/useSubmitSharedFileModal";
import { useUploadFileModal } from "../../components/useUploadFileModal";
import { getSharedFilePayeeRequirementStatus } from "../helpers/getSharedFilePayeeRequirementStatus";
import { getSharedFileProgressSteps } from "../helpers/getSharedFileProgressSteps";
import { getSharedFileRequirementTitle } from "../helpers/getSharedFileRequirementTitle";
import { mapContextTypeExtractedFieldsToDataView } from "../helpers/mapContextTypeExtractedFieldsToDataView";
import { renderSharedFileName } from "../helpers/renderSharedFileName";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../utils";
import { payerSharedFileRenderCardDescription } from "./payerSharedFileRenderCardDescription";

const MAX_REUPLOAD_ATTEMPTS = 3;

export const usePayerSharedFileRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  ISharedFileRequestResponse | undefined
> => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();
  const uploadFileModal = useUploadFileModal();
  const submitWithIssuesModal = useSubmitSharedFileModal();
  const [downloadDocument] = useDownloadPrivateDocument();

  const handleSubmitAnyway = async (
    requirement: IRequirementResponse,
    sharedFile: ISharedFileRequestResponse,
    context: ContextItem,
    onSuccess?: () => void
  ) => {
    await submitWithIssuesModal.open({
      sharedFileRequestId: sharedFile.sharedFileRequestId,
      sharedFileRequestSettingsId: sharedFile.sharedFileRequestSettingsId,
      payeeId: requirement.payeeId,
      payerId: requirement.payerId,
      onSubmit: () => {
        onSuccess?.();
        callback(SHARED_FILE_REQUIREMENT_EVENT.SubmitFileAnyway, requirement);
      }
    });
  };

  const handleRequirementDownload = async (
    sharedDocument: ISharedFileRequestResponse,
    requirement: IRequirementResponse
  ) => {
    const file = await filesService.member.private.get(sharedDocument.fileId!);

    await downloadDocument(file.fileId, {
      onSuccess: blob => {
        downloadFileFromBuffer(blob, file?.filename, file.mimetype);
        openSnackbar({
          message: "File downloaded successfully",
          type: "success"
        });
        callback(SHARED_FILE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
      },
      onError: () => {
        openSnackbar({
          message: "Download failed",
          type: "warning"
        });
      }
    });
  };

  const handleUploadFile = async (
    requirement: IRequirementResponse,
    sharedFile: ISharedFileRequestResponse,
    context: ContextItem
  ) => {
    await uploadFileModal.open({
      sharedFileRequestId: sharedFile.sharedFileRequestId,
      sharedFileRequestSettingsId: sharedFile.sharedFileRequestSettingsId,
      payeeId: requirement.payeeId,
      payerId: requirement.payerId,
      isCOI: selectorIsSharedFileCOIContext(sharedFile.contextType),
      attempts: selectorSharedFileReUploadAttempts(sharedFile),
      onSuccess: fileId => {
        callback(SHARED_FILE_REQUIREMENT_EVENT.UploadFile, requirement);
      }
    });
  };

  const getUploadFileAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    sharedFile: ISharedFileRequestResponse | undefined
  ) => {
    if (selectorIsRequirementReadyToUploadFile(requirement, sharedFile)) {
      return async () => {
        await handleUploadFile(requirement, sharedFile!, context);
      };
    }

    return undefined;
  };

  const getDownloadFileAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    sharedFile: ISharedFileRequestResponse | undefined
  ) => {
    const isDocumentReadyToDownload = selectorIsRequirementReadyToDownloadFile(
      requirement,
      sharedFile
    );

    if (isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(sharedFile!, requirement);
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, sharedFile) => {
      return !selectorIsSharedFileReUploadFlow(sharedFile)
        ? selectorIsRequirementReadyToUploadFile(requirement, sharedFile)
        : selectorIsSharedFileReUploadAvailable(sharedFile);
    },
    requirementDetailsProgressWidget: (requirement, context, sharedFile) => {
      return sharedFile
        ? getSharedFileProgressSteps(sharedFile.status, sharedFile.contextType)
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, sharedFile) => {
      return sharedFile ? (
        <SharedFileDetailsWidgetsContainer
          sharedFileRequestId={sharedFile.sharedFileRequestId}
          contextType={RequirementContextType.Payer}
          requirement={requirement}
          onSuccess={callback}
        />
      ) : null;
    },
    requirementInstructionsWidgets: (requirement, context, sharedFile) => {
      return sharedFile ? (
        <SharedFileInstructionsWidgetsContainer
          sharedFileRequest={sharedFile}
          contextType={RequirementContextType.Payer}
        />
      ) : null;
    },
    requirementInstructions: (requirement, context) => {
      return instructions => {
        return <RequirementInstructionsView instructions={instructions} />;
      };
    },
    requirementName: getSharedFileRequirementTitle,
    requirementStatusAlert: (requirement, context, sharedFile) => {
      if (selectorIsRequirementRejected(requirement)) {
        return {
          theme: "error",
          title: "Rejected",
          children:
            "The client rejected the submission and the requirement is marked incomplete.",
          icon: "alert-circle"
        };
      }

      if (
        selectorIsSharedFileNotNoneContext(sharedFile?.contextType) &&
        sharedFile?.status === SharedFileRequestStatus.PendingSubmission
      ) {
        if (!selectorIsSharedFileReUploadAvailable(sharedFile)) {
          return {
            theme: "warning",
            title: "Action required",
            children: (
              <>
                The uploaded file failed to pass all validations and you’ve
                reached the maximum upload limit. You have the following
                options:
                <WSElement as="ul" ml="L">
                  <li>
                    Submit the invalidated file anyway for review by client
                  </li>
                  <li>
                    Contact the client to lift the limit and re-request an
                    upload
                  </li>
                </WSElement>
              </>
            ),
            icon: "alert",
            action: {
              text: "Submit for review",
              onClick: () => {
                handleSubmitAnyway(requirement, sharedFile!, context);
              }
            }
          };
        }

        const attempts =
          MAX_REUPLOAD_ATTEMPTS -
          selectorSharedFileReUploadAttempts(sharedFile);

        return {
          theme: "warning",
          title: "Action required",
          children: (
            <>
              The uploaded file failed to pass all validations. You have the
              following options:
              <WSElement as="ul" ml="L">
                <li>
                  Re-upload a valid document to try again. You have {attempts}{" "}
                  attempts remaining before the requirement is marked as failed
                  to be further reviewed by the client.
                </li>
                <li>Submit the invalidated file anyway for review by client</li>
              </WSElement>
            </>
          ),
          icon: "alert",
          action: {
            text: "Upload new file",
            onClick: () => {
              handleUploadFile(requirement, sharedFile!, context);
            }
          }
        };
      }

      if (sharedFile?.status === SharedFileRequestStatus.Failed) {
        return {
          theme: "error",
          title: "Failed",
          children:
            "The uploaded file was submitted without passing all validations.",
          icon: "alert"
        };
      }

      return {
        theme: "warning",
        title: "Action required",
        children:
          "Complete the requirement in order to be eligible to receive payments from your client.",
        icon: "alert"
      };
    },
    requirementAdditionalContent: (requirement, context, sharedFile) => {
      const extractedFields = selectorSharedFileExtractedFields(
        sharedFile
      ) as ICOIExtractionInterface;

      if (extractedFields?.policies?.length) {
        return <SharedFilePolicies policies={extractedFields.policies} />;
      }
    },
    requirementDetails: (requirement, context, sharedFile) => {
      const uploadAction = getUploadFileAction(
        requirement,
        context,
        sharedFile
      );
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFile
      );
      const extractedFields = selectorSharedFileExtractedFields(sharedFile);

      return {
        header: {
          label: {
            text: renderSharedFileName(
              requirement,
              context,
              sharedFile
            ) as string
          }
        },
        data: extractedFields
          ? mapContextTypeExtractedFieldsToDataView(
              extractedFields,
              sharedFile?.contextType
            )
          : [],
        children: payerSharedFileRenderCardDescription(requirement, sharedFile),
        actions: {
          alignment: "fill",
          buttons: [
            {
              label: "Download file",
              onAsyncClick: downloadAction,
              kind: "Secondary",
              visible: !!downloadAction
            },
            {
              label: "Upload file",
              onAsyncClick: uploadAction,
              kind: "Primary",
              visible: !!uploadAction
            },
            {
              label: "Upload new file",
              onAsyncClick: async () => {
                await handleUploadFile(requirement, sharedFile!, context);
              },
              kind: "Primary",
              disabled: !selectorIsSharedFileReUploadAvailable(sharedFile),
              visible: selectorIsSharedFileReUploadFlow(sharedFile)
            }
          ]
        }
      };
    },
    toolbarActions: (requirement, context, sharedFile) => {
      const actions: RequirementAction[] = [];

      const uploadFileAction = getUploadFileAction(
        requirement,
        context,
        sharedFile
      );
      const downloadFileAction = getDownloadFileAction(
        requirement,
        context,
        sharedFile
      );

      if (sharedFile && selectorIsSharedFileReUploadFlow(sharedFile)) {
        actions.push({
          label: "Submit anyway",
          onAsyncClick: async () => {
            await handleSubmitAnyway(requirement, sharedFile, context);
          },
          buttonKind: "Tertiary",
          icon: "send",
          buttonIcon: null,
          disabled:
            sharedFile?.status !== SharedFileRequestStatus.PendingSubmission,
          mainAction: true
        });

        actions.push({
          label: "Upload new file",
          onAsyncClick: async () => {
            await handleUploadFile(requirement, sharedFile!, context);
          },
          buttonKind: "Primary",
          disabled: !selectorIsSharedFileReUploadAvailable(sharedFile),
          icon: "file",
          buttonIcon: null,
          mainAction: true
        });
      } else {
        if (uploadFileAction) {
          actions.push({
            onAsyncClick: uploadFileAction,
            label: "Upload file",
            buttonKind: "Primary",
            icon: "file",
            buttonIcon: null,
            mainAction: true
          });
        }
      }

      if (downloadFileAction) {
        actions.push({
          onAsyncClick: downloadFileAction,
          label: "Download file",
          buttonKind:
            sharedFile?.status === SharedFileRequestStatus.PendingSubmission
              ? undefined
              : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, sharedFile) => {
      return {
        text: getSharedFileRequirementTitle(requirement, context, sharedFile)
      };
    },
    typeCell: (requirement, _, sharedFile) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType,
        contextType: sharedFile?.contextType
      }),
    statusCell: (requirement, context, sharedFile) => {
      return getSharedFilePayeeRequirementStatus(
        requirement,
        context,
        sharedFile
      );
    }
  };
};
